import { Typography } from "@/components/text";
import { BackAppBar } from "@/components/ui";
import useChannel from "@/hooks/useChannel";
import useProfile from "@/hooks/useProfile";
import { getStatusColor } from "@/libraries/colors";
import { capitalized, genChannelName } from "@/libraries/helper";
import { getMemberQueryOptions } from "@/queries";
import MessageIcon from "@mui/icons-material/Message";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import MemberDetailSkeleton from "./-components/MemberDetailSkeleton";
import { getContactPeopleLoader } from "./-functions/loaders/peoples";

export const Route = createFileRoute("/dashboard/contacts/member/$id")({
	loader: (ctx) => getContactPeopleLoader(ctx.context, ctx.params.id),
	pendingComponent: MemberDetailSkeleton,
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const { profile } = useProfile();
	const memberId = Route.useParams({
		select: (params) => +params.id,
	});
	const { data: resMember } = useSuspenseQuery(getMemberQueryOptions(memberId));
	const user = resMember?.user;
	const name = user?.fullname || user?.first_name || user?.last_name || "L";
	const isMe = profile?.id === user?.id;

	const { mutateCreateChannel } = useChannel();

	const onClickMessage = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		let channelName = resMember.user.channel_name;
		if (!resMember.user.channel_name) {
			const resNewChannel = await mutateCreateChannel.mutateAsync({
				name: genChannelName("single"),
				messageable_id: resMember.user.id.toString(),
				messageable_type: "User",
			});
			channelName = String(resNewChannel.user.channel_name);
		}
		return navigate({
			to: "/dashboard/chat/single/$id/$channelName",
			params: {
				id: resMember.user.id,
				channelName,
			},
		});
	};

	return (
		<div className="relative h-dvh bg-white flex flex-col">
			<div className="bg-gradient-member h-[24.375rem]">
				<BackAppBar
					className="[&_*_]:text-white"
					bgColor="transparent"
					toolbarClassName="justify-between"
					rightContent={
						!isMe ? (
							<IconButton
								color="default"
								aria-label="Message"
								onClick={onClickMessage}
							>
								<MessageIcon />
							</IconButton>
						) : null
					}
				/>
			</div>
			<div className="container flex flex-col gap-2 justify-center items-center -translate-y-24">
				<Avatar
					alt={name}
					className="!bg-zinc-200 !text-dark !w-48 !h-48 !text-3xl"
					src={user?.avatar ?? "/broken-image.jpg"}
				/>
				<div className="flex flex-col gap-1 justify-center items-center">
					<Typography fontWeight={700} fontSize={22} align="center">
						{user?.fullname}
					</Typography>
					{user?.profession ? (
						<Typography fontWeight={600} fontSize={16} align="center">
							{user?.profession}
						</Typography>
					) : null}
					{user?.hospital?.name ? (
						<Typography fontWeight={400} fontSize={16} align="center">
							{user?.hospital?.name}
						</Typography>
					) : null}
				</div>
				<div className="rounded-full p-2 bg-[#F4F4F5] flex gap-2 justify-center items-center">
					<div
						className="w-2 h-2 rounded-full"
						style={{
							backgroundColor: getStatusColor(user?.status),
						}}
					/>
					<Typography fontWeight={400} fontSize={12} component="span">
						{capitalized(user?.status)}
					</Typography>
				</div>
			</div>
		</div>
	);
}
